import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, Input, OnInit, PLATFORM_ID, SimpleChanges, OnChanges } from '@angular/core';
import { Router } from '@angular/router';
import { GetMembershipsByMemberAndPartnerId, Membership } from '@fitscovery/membership/data-access';
import { GetPartnerBySubdomain, Partner, PartnerState, GetPartnerActiveMembershipSubscriptionsByPartnerId } from '@fitscovery/partner/data-access';
import { User, UserState, UserActions, USER_STATE_TOKEN } from '@fitscovery/user/data-access';
import { WINDOW } from '@ng-web-apis/common';
import { Select, Store } from '@ngxs/store';
import { environment } from 'apps/partner-web/src/environments/environment';
import { SetLoggedInUser, SignOutRevokeUserToken, Logout } from '@fitscovery/auth/data-access';
import { BehaviorSubject, combineLatest, EMPTY, Observable } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { SubscriptionStore } from '@fitscovery/common/classes/subscription-store';
import { AuthState } from '@fitscovery/auth/data-access';
import { StoreState, Language, StoreActions } from '@fitscovery/shared/store';
import { UtilSubdomain } from '@fitscovery/partner-web/shared/utils';
import { SnackbarService } from '@fitscovery/ui/snackbar';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Actions, ofActionDispatched } from '@ngxs/store';

@Component({
  selector: 'fitscovery-ui-navbar',
  templateUrl: './ui-navbar.component.html',
  styleUrls: ['./ui-navbar.component.scss']
})
export class UiNavbarComponent extends SubscriptionStore implements OnInit, OnChanges {
  @Input() component: string;
  @Select(UserState.user) userState$: Observable<User>;
  @Select(PartnerState.partner) partnerState$: Observable<Partner>;
  @Select(AuthState.token) token$: Observable<string>;
  @Select(StoreState.language) language$: Observable<Language>;

  accountUrl: string = environment.fitscoveryApps.accountUrl;
  isHomepage: boolean;
  isMobileNavOpen: boolean;
  myDiscoveryUrl: string = environment.fitscoveryApps.myDiscoveryUrl;
  memberships: Membership[];
  isSigningOut: boolean;

  partnerSubjectSource$: BehaviorSubject<Partner | null>;
  partnerSubject$: Observable<Partner | null>;

  isServer: boolean;
  isBrowser: boolean;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(WINDOW) private window: any,
    private actions$: Actions,
    private subdomain: UtilSubdomain,
    private router: Router,
    private store: Store,
    private cd: ChangeDetectorRef,
    private snackbar: SnackbarService
  ) {
    super();

    this.isServer = isPlatformServer(this.platformId);
    this.isBrowser = isPlatformBrowser(this.platformId);

    this.partnerSubjectSource$ = new BehaviorSubject<Partner | null>(null);
    this.partnerSubject$ = this.partnerSubjectSource$.asObservable().pipe( filter(e => !!e) );

    this.actions$
      .pipe(ofActionDispatched(Logout))
      .subscribe(() => { this.isSigningOut = false; });
  }

  ngOnInit(): void {
    this.isMobileNavOpen = false;

    this.token$.pipe(
      filter(Boolean),
      filter(() => {
        const token = this.store.selectSnapshot(e => e.auth.token);
        const helper = new JwtHelperService();
        return !helper.isTokenExpired(token);
      }),
      switchMap(() => this.store.dispatch(new UserActions.GetCurrentUser).pipe(
        catchError(() => { this.snackbar.destroyAll(); return EMPTY; })
      )),
      tap(() => {
        const authUser = this.store.selectSnapshot(e => e.auth.user);
        const user = this.store.selectSnapshot(USER_STATE_TOKEN).info;
        this.store.dispatch(new SetLoggedInUser({ ...authUser, ...user }));
      }),
    ).subscribe();

    this.partnerState$.subscribe((partner) => {

      if (isPlatformServer(this.platformId)) {
        this.partnerSubjectSource$.next(partner);
      } 
      
      if (isPlatformBrowser(this.platformId)) {
        this.partnerSubjectSource$.next(partner);
      }
      
    });

    const subdomain = this.subdomain.getSubdomain();
    this.store.dispatch(new GetPartnerBySubdomain(subdomain)); 

    this.partnerState$.pipe(
      filter(Boolean),
      take(1),
      tap((partner: Partner) => {
        this.store.dispatch(new GetPartnerActiveMembershipSubscriptionsByPartnerId(partner.id!));
      })
    ).subscribe();

    combineLatest([
      this.partnerState$,
      this.userState$
    ]).pipe(
      map((data: [ Partner, User ]) => ({ partnerId: data[0]?.id, memberId: data[1]?.id })),
      filter(({ partnerId, memberId }) => !!partnerId && !!memberId),
      tap(({ partnerId, memberId }) => this.store.dispatch(new GetMembershipsByMemberAndPartnerId(memberId!, partnerId!))),
      take(1)
    ).subscribe()
  }

  ngOnChanges(changes: SimpleChanges) {
    const url: string = this.router.url;
    if (!url) {
      this.cd.detectChanges();
    } else {
      this.getComponent(url);
    }
  }

  changeLanguage(language: Language) {
    this.store.dispatch(new StoreActions.ChangeLanguage(language));
  }

  private getComponent(url: string): void {
    if (url === '/') {
      this.isHomepage = true;
    } else {
      this.isHomepage = false;
    }

    const nav = this.document.getElementsByTagName('nav')[0];
    this.setNavClassList(nav, this.isHomepage);
    this.onScroll(nav, this.isHomepage);
  }

  private setNavClassList(nav: HTMLElement, isHomepage: boolean): void {
    if (nav) {
      if (isHomepage === true) {
        nav.classList.add('nav-transparent');
        nav.classList.remove('nav-shadow');
      } else {
        nav.classList.remove('nav-transparent');
        nav.classList.add('nav-shadow');
      }
    }
  }

  private onScroll(nav: HTMLElement, isHomepage: boolean): void {
    if (isPlatformBrowser(this.platformId)) {
      if (nav) {
        if (isHomepage === true) {
          this.window.onscroll = () => {
            if (this.window.scrollY <= 160) {
              nav.classList.add('nav-transparent');
              nav.classList.remove('nav-shadow');
            } else {
              nav.classList.remove('nav-transparent');
              nav.classList.add('nav-shadow');
            }
          };
        } else {
          this.window.onscroll = () => {
            nav.classList.remove('nav-transparent');
          };
        }
      }
    }
  }

  async login(): Promise<void> {
    if (this.isSigningOut) return;
    this.isSigningOut = true;
    try {
      // this.httpAuthService.warmUpRequest();
      // redirect the user to authenticate page.
      // authenticate page will handle the authentication part
      // const redirectUrl: string = encodeURIComponent(this.urlService.getFullUrl());
      const redirectUrl: string = encodeURIComponent(this.window.location.href);
      this.router.navigate(['/auth/authenticate'], { queryParams: { redirectUrl: redirectUrl } });
    } catch (error) {
      console.error(error);
    }
  }

  async logout(): Promise<void> {
    this.isSigningOut = true;
    this.store.dispatch(new SignOutRevokeUserToken);
  }
}
